/**
 * Replaces numerical segments in a URL path with the placeholder `{id}` so
 * errors that vary only by entity ID get grouped. Numbers immediately after
 * `/api/` are preserved (they are API versions, not IDs). Query parameter
 * values that are purely numeric are also replaced.
 *
 * Same-origin and relative URLs are reduced to path + query so they group
 * across hosts; cross-origin URLs keep their scheme + host so third-party
 * endpoints stay distinguishable from our own API paths.
 *
 * Example:
 *   /api/3/media2/85994393674/view?user=123 -> /api/3/media2/{id}/view?user={id}
 */
// Values of these query parameters vary per request (video ids, IPs, free text,
// upload tokens, filenames) and are replaced regardless of format.
const volatileQueryParams = [
	"contentIds",
	"ipAddress",
	"query",
	"meetingId",
	"filename",
	"uploadId",
];

export const generalizeUrl = (url: string, preserveQueryParams: readonly string[] = []): string => {
	if (!url) return "";

	let urlObj: URL;
	try {
		urlObj = new URL(url, "http://dummy.com/");
	} catch {
		return url;
	}

	let originPrefix = "";
	const isProtocolRelative = url.startsWith("//");
	const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(url);
	if (urlObj.host && (hasScheme || isProtocolRelative)) {
		const loc = typeof window !== "undefined" ? window.location : undefined;
		if (isProtocolRelative) {
			if (urlObj.host !== loc?.host) originPrefix = `//${urlObj.host}`;
		} else if (urlObj.origin !== loc?.origin) {
			originPrefix = `${urlObj.protocol}//${urlObj.host}`;
		}
	}

	const originalSegments = urlObj.pathname.split("/");
	const generalizedSegments = originalSegments.map((segment, index) => {
		if (segment === "") return segment;
		// A comma-separated list of ids collapses into a single placeholder.
		if (/^\d+(?:,\d+)*$/.test(segment)) {
			if (originalSegments[index - 1]?.toLowerCase() === "api") return segment;
			return "{id}";
		}
		if (/^[a-f\d]{32,}$/i.test(segment)) return "{id}";
		return segment;
	});

	const generalizedPath = generalizedSegments.join("/") || "/";

	const generalizedSearch = new URLSearchParams();
	for (const [key, value] of urlObj.searchParams.entries()) {
		const generalize =
			!preserveQueryParams.includes(key) &&
			(volatileQueryParams.includes(key) || /^\d+(?:,\d+)*$/.test(value));
		generalizedSearch.append(key, generalize ? "{id}" : value);
	}
	const search = generalizedSearch.toString();

	const combined = originPrefix + generalizedPath + (search ? "?" + search : "");
	try {
		return decodeURIComponent(combined);
	} catch {
		return combined;
	}
};
